// File: src/store/slices/packagesSlice.js
// Purpose: Redux Toolkit slice for packages state management
// Last modified: 2025-11-06

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';
import packageCache from '../../utils/packageCache';

// ==================== Async Thunks ====================

/**
 * Fetch all packages
 */
export const fetchPackages = createAsyncThunk(
    'packages/fetchAll',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { packages } = getState().packages;

            if (packages && packages.length > 0) {
                return packages;
            }

            const data = await apiService.packages.getAll();
            return data;
        } catch (error) {
            console.error('[fetchPackages] Error:', error);
            return rejectWithValue(error.message);
        }
    },
    {
        condition: (_, { getState }) => {
            const { packages, loading } = getState().packages;
            if (packages && packages.length > 0) return false;
            if (loading) return false;
            return true;
        },
    }
);

/**
 * Fetch single package with caching
 */
export const fetchPackage = createAsyncThunk(
    'packages/fetchById',
    async (packageId, { rejectWithValue }) => {
        try {
            // Try cache first
            const cachedData = packageCache.get(packageId);
            if (cachedData) {
                console.log('[fetchPackage] Using cached data for:', packageId);
                return cachedData;
            }

            // Fetch from API
            console.log('[fetchPackage] Fetching from API for:', packageId);
            const data = await apiService.packages.getById(packageId);

            // Store in cache
            packageCache.set(packageId, data);

            return data;
        } catch (error) {
            console.error('[fetchPackage] Error:', error);
            return rejectWithValue(error.message);
        }
    }
);

// ==================== Slice ====================

const initialState = {
    packages: [],
    currentPackage: null,
    loading: false,
    error: null,
};

const packagesSlice = createSlice({
    name: 'packages',
    initialState,
    reducers: {
        clearCurrentPackage: (state) => {
            state.currentPackage = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch all packages
            .addCase(fetchPackages.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPackages.fulfilled, (state, action) => {
                state.loading = false;
                state.packages = action.payload;
            })
            .addCase(fetchPackages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.packages = [];
            })
            // Fetch single package
            .addCase(fetchPackage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPackage.fulfilled, (state, action) => {
                state.loading = false;
                state.currentPackage = action.payload;
            })
            .addCase(fetchPackage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.currentPackage = null;
            });
    },
});

// ==================== Exports ====================

export const { clearCurrentPackage, clearError } = packagesSlice.actions;

// Selectors
export const selectPackages = (state) => state.packages.packages;
export const selectCurrentPackage = (state) => state.packages.currentPackage;
export const selectPackagesLoading = (state) => state.packages.loading;
export const selectPackagesError = (state) => state.packages.error;

export default packagesSlice.reducer;
